import { Component, signal, computed, inject, WritableSignal, ElementRef, HostListener, DestroyRef, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { SearchbarComponent } from 'src/app/shared/components/searchbar/searchbar.component';
import { TranslateModule } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ResetSearch, SearchByTerm } from 'src/app/search/state/search.actions';
import { debounceTime, distinctUntilChanged, filter, map, tap } from 'rxjs';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { TrackingService } from 'src/app/shared/services/tracking.service';
import { TRACKING_ITEM_TYPE } from 'src/app/shared/types/tracking.type';
import { HeaderSearchResultsComponent } from './header-search-results/header-search-results.component';

@Component({
  selector: 'app-header-search',
  imports: [SearchbarComponent, TranslateModule, HeaderSearchResultsComponent],
  templateUrl: './header-search.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './header-search.component.scss'
})
export class HeaderSearchComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly trackingService = inject(TrackingService);

  protected currentSearchString = signal<string>('');

  protected showResults = computed(() => this.currentSearchString().length > 0 && this.searchFocused());
  private searchFocused: WritableSignal<boolean> = signal<boolean>(false);

  private searchTerm$ = toObservable(this.currentSearchString);

  ngOnInit(): void {
    this.listenToSearchInput();
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const container = this.elementRef.nativeElement.querySelector('.header-search-container');
    if (!container.contains(event.target)) {
      this.searchFocused.set(false);
    }
  }

  protected onSearchInputChange(searchTerm: string): void {
    this.currentSearchString.set(searchTerm);
  }

  protected onContainerFocusIn(): void {
    this.searchFocused.set(true);
  }

  protected onCloseSearch(): void {
    this.searchFocused.set(false);
  }

  private listenToSearchInput(): void {
    this.searchTerm$
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        tap(searchTerm => this.store.dispatch(searchTerm ? new SearchByTerm(searchTerm) : new ResetSearch())),
        map(searchTerm => searchTerm),
        filter(searchTerm => !!searchTerm),
        tap(searchTerm => this.trackingService.addWCSTrackingItem(TRACKING_ITEM_TYPE.APP_SEARCH, searchTerm)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }
}
